import { supabase } from "../supabase";

// Which of a page's design ids the current viewer has liked/saved. This no
// longer lives on the design row itself (see supabase/likes-saves-tables-migration.sql)
// — fetched once per page load (or page-of-results), in parallel with the
// designs query itself, the same way Home.jsx already runs its feed queries
// together via Promise.all.
export async function fetchMyEngagement(designIds, userId) {
  if (!userId || !designIds || designIds.length === 0) {
    return { likedIds: new Set(), savedIds: new Set() };
  }

  const [{ data: likedRows, error: likedError }, { data: savedRows, error: savedError }] = await Promise.all([
    supabase.from("likes").select("design_id").eq("user_id", userId).in("design_id", designIds),
    supabase.from("saves").select("design_id").eq("user_id", userId).in("design_id", designIds)
  ]);

  if (likedError) console.log("FETCH MY LIKES ERROR:", likedError);
  if (savedError) console.log("FETCH MY SAVES ERROR:", savedError);

  return {
    likedIds: new Set((likedRows || []).map(r => r.design_id)),
    savedIds: new Set((savedRows || []).map(r => r.design_id))
  };
}

// Live updates for the CURRENT viewer's own likes/saves only — filtered with
// a plain `user_id=eq.`, unlike the page's shared `designs` UPDATE listener
// (which has no way to scope to "just the posts on screen" and filters
// client-side instead), this only ever fires for this one person's own rows.
// Covers the one thing the old liked_by/saved_by arrays had no live path for
// either: another tab or device signed into the same account liking or
// saving something.
// Attaches the four bindings above to an existing channel (chainable, does
// not call .subscribe()) — used by pages that merge this with their own
// page-specific listener onto one channel instead of opening a second.
export function attachMyEngagementBindings(channel, userId, { onLike, onUnlike, onSave, onUnsave } = {}) {
  return channel
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "likes", filter: `user_id=eq.${userId}` },
      payload => onLike?.(payload.new.design_id)
    )
    .on(
      "postgres_changes",
      { event: "DELETE", schema: "public", table: "likes", filter: `user_id=eq.${userId}` },
      payload => onUnlike?.(payload.old.design_id)
    )
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "saves", filter: `user_id=eq.${userId}` },
      payload => onSave?.(payload.new.design_id)
    )
    .on(
      "postgres_changes",
      { event: "DELETE", schema: "public", table: "saves", filter: `user_id=eq.${userId}` },
      payload => onUnsave?.(payload.old.design_id)
    );
}

export function subscribeToMyEngagement(userId, handlers = {}) {
  if (!userId) return () => {};

  const channel = attachMyEngagementBindings(supabase.channel(`my-engagement-${userId}`), userId, handlers).subscribe();

  return () => supabase.removeChannel(channel);
}
